import BreadCrumbs from '@components/Breadcrumbs';
import Button from '@components/Button';
import DatePicker from '@components/DatePicker';
import TextField from '@components/TextField';
import TimePicker from '@components/TimePicker';
import Icon from '@material-ui/core/Icon';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Alert from '@material-ui/lab/Alert';
import { apiClient } from '@services/api';
import { autoDiscountLabel } from '@utils/blackFriday';
import { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';

/* ── paleta ───────────────────────────────── */
const C = {
  bg:      '#0f1e35',
  card:    '#172B4D',
  inner:   '#0f2040',
  accent:  '#62DEC2',
  textSec: '#8ba5c4',
  textMain:'#e8f0fb',
  border:  '#1e3a5f',
  danger:  '#ef4444',
};

/* ── componente contador ──────────────────── */
const Counter = ({ value, min = 0, max = 20, onChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
    <button
      onClick={() => value > min && onChange(value - 1)}
      disabled={value <= min}
      style={{
        width: 28, height: 28, borderRadius: 6, border: `1px solid ${C.border}`,
        background: value <= min ? 'transparent' : C.inner,
        color: value <= min ? C.border : C.accent,
        cursor: value <= min ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 18, fontWeight: 700,
      }}
    >−</button>
    <span style={{ color: C.textMain, fontWeight: 600, minWidth: 20, textAlign: 'center' }}>
      {value}
    </span>
    <button
      onClick={() => value < max && onChange(value + 1)}
      disabled={value >= max}
      style={{
        width: 28, height: 28, borderRadius: 6, border: `1px solid ${C.border}`,
        background: value >= max ? 'transparent' : C.inner,
        color: value >= max ? C.border : C.accent,
        cursor: value >= max ? 'default' : 'pointer',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 18, fontWeight: 700,
      }}
    >+</button>
  </div>
);

const Update = (props) => {
  document.title = `Editar orçamento - ${process.env.REACT_APP_NAME}`;

  const { presales_id } = useParams();
  const history = useHistory();

  const [presale,      setPresale]      = useState(null);
  const [budgets,      setBudgets]      = useState([]);
  const [client,       setClient]       = useState({});
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState(null);
  const [locked,       setLocked]       = useState(false);
  const [lockReason,   setLockReason]   = useState('');

  // Vendedor parceiro
  const [partnerSeller,     setPartnerSeller]     = useState(null);  // { id, name, email, type }
  const [partnerSearch,     setPartnerSearch]     = useState('');
  const [partnerOptions,    setPartnerOptions]    = useState([]);
  const [partnerLoading,    setPartnerLoading]    = useState(false);
  const [showPartnerSearch, setShowPartnerSearch] = useState(false);

  // Modal: adicionar serviço
  const [addModal,     setAddModal]     = useState(false);
  const [addType,      setAddType]      = useState('tour');
  const [addSearch,    setAddSearch]    = useState('');
  const [allServices,  setAllServices]  = useState({ tour: [], transfer: [] });
  const [addResults,   setAddResults]   = useState([]);
  const [addSelected,  setAddSelected]  = useState(null);
  const [addAdults,        setAddAdults]        = useState(1);
  const [addKids,          setAddKids]          = useState(0);
  const [addDate,          setAddDate]          = useState(null);
  const [addTransferType,  setAddTransferType]  = useState('Transfer In');
  const [addCodFly,        setAddCodFly]        = useState('');
  const [addTimeFly,       setAddTimeFly]       = useState(null);
  const [addSaving,        setAddSaving]        = useState(false);
  const [addLoading,       setAddLoading]       = useState(false);
  const [availableDates,   setAvailableDates]   = useState([]);
  const [itemAvailDates,   setItemAvailDates]   = useState({}); // { [tour_id]: [] }
  const [addModality,      setAddModality]      = useState('group');   // group | single
  const [addPrivateValues, setAddPrivateValues] = useState([]);        // faixas do tour privado

  /* ── carrega presale + budgets ────────── */
  useEffect(() => {
    apiClient.httpInstance.get(`/presales/${presales_id}`).then(res => {
      const p = res.data;
      setPresale(p);
      setClient({
        name:     p.name,
        email:    p.email,
        phone:    p.phone,
        document: p.document,
      });

      // Carrega vendedor parceiro se existir
      if (p.partner_seller_id && p.partner_seller_type) {
        const url = p.partner_seller_type === 'agency'
          ? `/agencies/${p.partner_seller_id}`
          : `/partner_sellers/${p.partner_seller_id}`;
        apiClient.httpInstance.get(url)
          .then(r => setPartnerSeller({ id: r.data.id, name: r.data.name || r.data.email, email: r.data.email, type: p.partner_seller_type }))
          .catch(() => {});
      }

      // Regras de bloqueio
      if (p.customer_status === 'approved') {
        setLocked(true);
        setLockReason('Este orçamento foi aprovado pelo cliente e não pode ser editado.');
        return;
      }
      if (p.customer_status === 'refused') {
        setLocked(true);
        setLockReason('Este orçamento foi recusado pelo cliente e não pode ser editado.');
        return;
      }
      if (p.status === 'sold') {
        setLocked(true);
        setLockReason('Este orçamento já foi convertido em venda e não pode ser editado.');
        return;
      }
      if (p.expires_at && new Date(p.expires_at) < new Date()) {
        setLocked(true);
        setLockReason('Este orçamento expirou e não pode ser editado.');
        return;
      }
    });

    apiClient.httpInstance.get(`/presales/${presales_id}/budgets`).then(res => {
      const items = res.data.map(b => ({ ...b }));
      setBudgets(items);
      // Carrega cupos disponíveis para cada tour nos itens existentes
      const tourIds = [...new Set(items.filter(b => b.tour_id && b.travel_type !== 'transfer').map(b => b.tour_id))];
      tourIds.forEach(tid => {
        apiClient.httpInstance
          .get(`reservations_dates/available?tour_id=${tid}`)
          .then(r => setItemAvailDates(prev => ({ ...prev, [tid]: r.data ?? [] })))
          .catch(() => {});
      });
    });
  }, [presales_id]);

  /* ── helpers de edição inline ─────────── */
  const updateBudget = (id, field, val) => {
    setBudgets(prev => prev.map(b => b.id === id ? { ...b, [field]: val } : b));
  };

  const removeBudget = (id) => {
    if (!window.confirm('Remover este serviço do orçamento?')) return;
    apiClient.httpInstance.delete(`/presales/${presales_id}/budgets/${id}`)
      .then(() => setBudgets(prev => prev.filter(b => b.id !== id)))
      .catch(() => setError('Erro ao remover serviço. Tente novamente.'));
  };

  /* ── salvar alterações ────────────────── */
  const save = () => {
    setSaving(true);
    apiClient.httpInstance.put(`/presales/${presales_id}/update-values`, {
      reservations:        budgets,
      name:                client.name,
      email:               client.email,
      phone:               client.phone,
      document:            client.document,
      partner_seller_id:   partnerSeller ? partnerSeller.id   : null,
      partner_seller_type: partnerSeller ? partnerSeller.type : null,
    })
      .then(() => history.push(`/admin/orcamentos/detalhe/${presales_id}`))
      .catch(() => {
        setError('Erro ao salvar. Tente novamente.');
        setSaving(false);
      });
  };

  /* ── carrega todos os serviços ao abrir modal ───── */
  useEffect(() => {
    if (!addModal) return;
    const type = addType;
    // já carregou esse tipo
    if (allServices[type === 'tour' ? 'tour' : 'transfer'].length > 0) return;
    setAddLoading(true);
    const endpoint = type === 'tour' ? '/tours' : '/translados';
    apiClient.httpInstance.get(endpoint, { params: { pageSize: 500, array: 'true' } })
      .then(res => {
        const list = res.data?.data ?? res.data ?? [];
        setAllServices(prev => ({ ...prev, [type === 'tour' ? 'tour' : 'transfer']: list }));
      })
      .catch(() => {})
      .finally(() => setAddLoading(false));
  }, [addModal, addType]); // eslint-disable-line

  /* ── filtra client-side ───── */
  useEffect(() => {
    const list = allServices[addType === 'tour' ? 'tour' : 'transfer'];
    if (!addSearch || addSearch.length < 2) {
      setAddResults(list.slice(0, 50));
      return;
    }
    const q = addSearch.toLowerCase();
    const filtered = list.filter(item =>
      (item.name        || '').toLowerCase().includes(q) ||
      (item.region_name || '').toLowerCase().includes(q)
    );
    setAddResults(filtered.slice(0, 50));
  }, [addSearch, addType, allServices]);

  const resetAddModal = () => {
    setAddSearch(''); setAddSelected(null);
    setAddAdults(1); setAddKids(0); setAddDate(null);
    setAddTransferType('Transfer In'); setAddCodFly(''); setAddTimeFly(null);
    setAvailableDates([]);
    setAddModality('group'); setAddPrivateValues([]);
  };

  // Busca cupos disponíveis + faixas de privado ao selecionar um tour
  useEffect(() => {
    if (!addSelected || addType !== 'tour') { setAvailableDates([]); setAddPrivateValues([]); setAddModality('group'); return; }
    apiClient.httpInstance
      .get(`reservations_dates/available?tour_id=${addSelected.id}`)
      .then(res => setAvailableDates(res.data ?? []))
      .catch(() => setAvailableDates([]));
    setAddModality('group');
    apiClient.httpInstance
      .get(`/tours/${addSelected.id}`)
      .then(res => setAddPrivateValues(res.data?.privateValues ?? res.data?.private_values ?? []))
      .catch(() => setAddPrivateValues([]));
  }, [addSelected, addType]);

  const disableDates = (date) => {
    if (availableDates.length === 0) return true; // sem info: permite todas
    let hasSlot = false;
    availableDates.forEach(d => {
      if (
        date.toDateString() === new Date(`${d.cupo_date}T00:00:00`).toDateString() &&
        d.total_cupos > 0
      ) { hasSlot = true; }
    });
    return hasSlot;
  };

  const submitAddBudget = () => {
    if (!addSelected) return;
    setAddSaving(true);

    // formata data para string YYYY-MM-DD
    const formatDate = (d) => {
      if (!d) return null;
      if (typeof d === 'string') return d.split('T')[0];
      try { return d.toISOString().split('T')[0]; } catch { return null; }
    };

    // formata horário do voo para "HH:mm" LOCAL (coluna flight_time é tipo TIME;
    // enviar o Date/ISO completo do TimePicker quebrava com erro 22007 no Postgres)
    const formatTime = (t) => {
      if (!t) return null;
      if (typeof t === 'string') {
        if (t.includes('T')) { const m = t.match(/T(\d{2}:\d{2})/); return m ? m[1] : null; }
        return t.slice(0, 5); // já vem "HH:mm" ou "HH:mm:ss"
      }
      try {
        const hh = String(t.getHours()).padStart(2, '0');
        const mm = String(t.getMinutes()).padStart(2, '0');
        return `${hh}:${mm}`;
      } catch { return null; }
    };

    // ── Regra PIX-exclusivo (tours #139 Monteluz e #58 Alyan Sunset): mês vigente → orçamento separado ──
    const PIX_ONLY_IDS  = [139, 58];
    const isPixOnly     = addType === 'tour' && PIX_ONLY_IDS.includes(addSelected?.id);
    const dateStr       = formatDate(addDate);
    const currentMonth  = new Date().toISOString().slice(0, 7);
    const isCurrentMonth = dateStr && dateStr.slice(0, 7) === currentMonth;

    if (isPixOnly && isCurrentMonth) {
      // PIX-only no mês vigente: só pode ter outros tours PIX-only no mesmo orçamento
      const hasOtherTours = budgets.some(b => b.travel_type === 'tour' && !PIX_ONLY_IDS.includes(b.tour_id));
      if (hasOtherTours) {
        setError('Monteluz e Alyan Sunset do mês vigente devem ir em um orçamento separado (somente PIX).');
        setAddSaving(false);
        return;
      }
    } else if (addType === 'tour' && !isPixOnly) {
      // Tour regular: não pode ir junto com PIX-only do mês vigente
      const hasPixOnlyCurrentMonth = budgets.some(b =>
        PIX_ONLY_IDS.includes(b.tour_id) &&
        b.tour_date && b.tour_date.slice(0, 7) === currentMonth
      );
      if (hasPixOnlyCurrentMonth) {
        setError('Monteluz e Alyan Sunset do mês vigente devem ir em um orçamento separado (somente PIX).');
        setAddSaving(false);
        return;
      }
    }
    // ── fim regra PIX-exclusivo ──

    // Privado: valida faixa antes de enviar
    if (addType === 'tour' && addModality === 'single') {
      const band = addPrivateValues.find(v => Number(v.min_people) <= addAdults && addAdults <= Number(v.max_people));
      if (!band) {
        setError(`Não existe intervalo de privado definido para ${addAdults} pessoa(s).`);
        setAddSaving(false);
        return;
      }
    }

    apiClient.httpInstance.post(`/presales/${presales_id}/budgets`, {
      type:               addType,
      service_id:         addSelected.id,
      modality:           addType === 'tour' ? addModality : null,
      adults:             addAdults,
      kids:               addType === 'tour' ? addKids : 0,
      tour_date:          formatDate(addDate),
      transfer_type:      addType === 'transfer' ? addTransferType : null,
      cod_fly:            addType === 'transfer' ? addCodFly : null,
      time_fly:           addType === 'transfer' ? formatTime(addTimeFly) : null,
    })
      .then(res => {
        setBudgets(prev => [...prev, res.data.budget]);
        setAddModal(false);
        resetAddModal();
      })
      .catch(e => setError(e?.response?.data?.message || 'Erro ao adicionar serviço.'))
      .finally(() => setAddSaving(false));
  };

  const breadcrumbs = [
    { title: 'Início', active: true, route: '/admin/orcamentos' },
    { title: `Orçamento ${client.name ?? ''}`, active: true, route: `/admin/orcamentos/detalhe/${presales_id}` },
    { title: 'Edição' },
  ];

  const inputStyle = {
    background: C.inner, border: `1px solid ${C.border}`, borderRadius: 6,
    padding: '6px 10px', color: C.textMain, fontSize: 13, outline: 'none', width: '100%',
    boxSizing: 'border-box',
  };

  return (
    <>
      <BreadCrumbs items={breadcrumbs} />
      <div style={{ padding: '24px 28px', background: C.bg, minHeight: '100vh' }}>

        {/* Banner PIX-exclusivo mês vigente — visível só para o consultor */}
        {budgets.some(b => [139, 58].includes(b.tour_id) && b.tour_date && b.tour_date.slice(0, 7) === new Date().toISOString().slice(0, 7)) && (
          <div style={{
            background: '#451a03', border: '1px solid #f97316', borderRadius: 10,
            padding: '12px 20px', marginBottom: 16, color: '#fed7aa', fontSize: 13,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <Icon style={{ fontSize: 18, color: '#f97316' }}>warning_amber</Icon>
            <span>
              <strong>Orçamento exclusivo Monteluz/Alyan — mês vigente.</strong>{' '}
              Somente PIX. Não é possível adicionar outros tours a este orçamento.
            </span>
          </div>
        )}

        {/* Banner de bloqueio */}
        {locked && (
          <div style={{
            background: '#7f1d1d', border: `1px solid ${C.danger}`, borderRadius: 10,
            padding: '14px 20px', marginBottom: 20, color: '#fecaca', fontSize: 14,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <Icon style={{ fontSize: 20 }}>lock</Icon>
            {lockReason}
          </div>
        )}

        {error && (
          <div style={{
            background: '#7f1d1d', border: `1px solid ${C.danger}`, borderRadius: 10,
            padding: '12px 16px', marginBottom: 16, color: '#fecaca', fontSize: 13,
          }}>
            {error}
          </div>
        )}

        <h2 style={{ color: C.textMain, fontSize: 20, fontWeight: 700, marginBottom: 20 }}>
          Edição do orçamento
        </h2>

        {/* ── Serviços ─────────────────────────────── */}
        <div style={{ background: C.card, borderRadius: 12, marginBottom: 16, overflow: 'hidden' }}>
          <div style={{
            padding: '14px 20px', borderBottom: `1px solid ${C.border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ color: C.textMain, fontWeight: 700, fontSize: 14 }}>
              Serviços incluídos
            </span>
            {!locked && (
              <button
                onClick={() => { setAddModal(true); setAddType('tour'); setAddSearch(''); setAddSelected(null); }}
                style={{
                  background: C.accent, border: 'none', borderRadius: 8,
                  padding: '6px 16px', color: '#0f1e35', fontWeight: 700,
                  fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
                }}
              >
                <Icon style={{ fontSize: 16 }}>add</Icon>
                Adicionar serviço
              </button>
            )}
          </div>

          {budgets.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: C.textSec, fontSize: 13 }}>
              Nenhum serviço. Adicione pelo menos um.
            </div>
          ) : budgets.map((b, i) => (
            <div key={b.id} style={{
              padding: '16px 20px',
              borderBottom: i < budgets.length - 1 ? `1px solid ${C.border}` : 'none',
            }}>
              {/* Nome do serviço */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
                <div>
                  <span style={{ color: C.accent, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1 }}>
                    {b.travel_type === 'transfer' ? 'Transfer' : 'Passeio'}
                  </span>
                  <div style={{ color: C.textMain, fontWeight: 600, fontSize: 15, marginTop: 2 }}>
                    {b.travel_name ?? b.transfer_type ?? `Serviço #${b.id}`}
                  </div>
                </div>
                {!locked && (
                  <button
                    onClick={() => removeBudget(b.id)}
                    style={{
                      background: 'transparent', border: `1px solid ${C.danger}`,
                      borderRadius: 6, padding: '4px 10px', color: C.danger,
                      cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 4,
                    }}
                  >
                    <Icon style={{ fontSize: 14 }}>delete</Icon>
                    Remover
                  </button>
                )}
              </div>

              {/* Campos editáveis */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
                {/* Data */}
                {b.travel_type !== 'transfer' && (
                  <div>
                    <label style={{ color: C.textSec, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                      Data do passeio
                    </label>
                    <DatePicker
                      label="Data"
                      disabled={locked}
                      value={b.tour_date ? `${b.tour_date.slice(0, 10)} 00:00:00` : null}
                      minDate={`${new Date().toISOString().slice(0, 10)} 00:00:00`}
                      shouldDisableDate={date => {
                        const avail = itemAvailDates[b.tour_id] || [];
                        if (avail.length === 0) return false;
                        let hasSlot = false;
                        avail.forEach(d => {
                          if (date.toDateString() === new Date(`${d.cupo_date}T00:00:00`).toDateString() && d.total_cupos > 0) hasSlot = true;
                        });
                        return !hasSlot;
                      }}
                      onChange={date => {
                        if (!date) return;
                        const d = new Date(date);
                        const fmt = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                        updateBudget(b.id, 'tour_date', fmt);
                      }}
                    />
                  </div>
                )}

                {/* Adultos */}
                <div>
                  <label style={{ color: C.textSec, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    Adultos
                  </label>
                  {locked
                    ? <span style={{ color: C.textMain }}>{b.adults_total}</span>
                    : <Counter
                        value={parseInt(b.adults_total) || 0}
                        min={b.travel_type === 'transfer' ? 1 : 0}
                        onChange={val => updateBudget(b.id, 'adults_total', val)}
                      />
                  }
                  {b.adults_total > 0 && b.adults_total_amount > 0 && (
                    <span style={{ color: C.textSec, fontSize: 11, marginTop: 4, display: 'block' }}>
                      {process.env.REACT_APP_CURRENCY} {(b.adults_total_amount / b.adults_total).toFixed(2).replace('.', ',')} /pessoa
                    </span>
                  )}
                </div>

                {/* Crianças (só passeios) */}
                {b.travel_type !== 'transfer' && (
                  <div>
                    <label style={{ color: C.textSec, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                      Crianças
                    </label>
                    {locked
                      ? <span style={{ color: C.textMain }}>{b.kids_total ?? 0}</span>
                      : <Counter
                          value={parseInt(b.kids_total) || 0}
                          onChange={val => updateBudget(b.id, 'kids_total', val)}
                        />
                    }
                    {b.kids_total > 0 && b.kids_total_amount > 0 && (
                      <span style={{ color: C.textSec, fontSize: 11, marginTop: 4, display: 'block' }}>
                        {process.env.REACT_APP_CURRENCY} {(b.kids_total_amount / b.kids_total).toFixed(2).replace('.', ',')} /criança
                      </span>
                    )}
                  </div>
                )}

                {/* Desconto automático por região */}
                <div>
                  <label style={{ color: C.textSec, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    Desc. automático
                  </label>
                  <span style={{ fontSize: 11, color: '#6e8eb0' }}>
                    {b.travel_type === 'transfer' ? '—' : autoDiscountLabel(b.region_name || b.travel_region, presale?.created_at)}
                  </span>
                  {b.adults_operational_cost > 0 && (
                    <span style={{ color: C.textSec, fontSize: 10, marginTop: 4, display: 'block' }}>
                      custo: {process.env.REACT_APP_CURRENCY} {Number(b.adults_operational_cost).toFixed(2).replace('.', ',')} /adulto
                    </span>
                  )}
                </div>

                {/* Total */}
                <div>
                  <label style={{ color: C.textSec, fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                    Total
                  </label>
                  <span style={{ color: C.accent, fontWeight: 700, fontSize: 15 }}>
                    {process.env.REACT_APP_CURRENCY} {Number(b.total_amount || 0).toFixed(2).replace('.', ',')}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Dados do cliente (editável) ───── */}
        <div style={{ background: C.card, borderRadius: 12, marginBottom: 24, padding: '16px 20px' }}>
          <div style={{ color: C.textSec, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>
            Dados do cliente
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            {[
              { key: 'name',     label: 'Nome',         type: 'text'  },
              { key: 'email',    label: 'E-mail',       type: 'email' },
              { key: 'phone',    label: 'Telefone',     type: 'text'  },
              { key: 'document', label: 'CPF/Passaporte', type: 'text' },
            ].map(({ key, label, type }) => (
              <div key={key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                <label style={{ color: C.textSec, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {label}
                </label>
                <input
                  type={type}
                  value={client[key] ?? ''}
                  disabled={locked}
                  onChange={e => setClient(prev => ({ ...prev, [key]: e.target.value }))}
                  style={{
                    background: C.cardInner ?? '#0f1c2e',
                    border: `1px solid ${C.sep ?? '#1a3050'}`,
                    borderRadius: 8,
                    padding: '8px 12px',
                    color: locked ? C.textSec : C.textMain,
                    fontSize: 13,
                    outline: 'none',
                    width: '100%',
                    boxSizing: 'border-box',
                    cursor: locked ? 'not-allowed' : 'text',
                  }}
                />
              </div>
            ))}
          </div>
        </div>

        {/* ── Vendedor parceiro ───────────────────── */}
        {!locked && (
          <div style={{ background: C.card, borderRadius: 12, marginBottom: 24, padding: '16px 20px' }}>
            <div style={{ color: C.textSec, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 14 }}>
              Vendedor parceiro
            </div>

            {partnerSeller ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.inner, borderRadius: 8, padding: '10px 14px' }}>
                <div>
                  <span style={{ color: C.textMain, fontWeight: 600, fontSize: 13 }}>{partnerSeller.name}</span>
                  {partnerSeller.email && <span style={{ color: C.textSec, fontSize: 12, marginLeft: 8 }}>({partnerSeller.email})</span>}
                </div>
                <button
                  onClick={() => setPartnerSeller(null)}
                  style={{ background: 'transparent', border: `1px solid ${C.danger}`, borderRadius: 6, color: C.danger, padding: '4px 12px', fontSize: 12, cursor: 'pointer' }}
                >
                  Remover parceiro
                </button>
              </div>
            ) : (
              <>
                {!showPartnerSearch ? (
                  <button
                    onClick={() => {
                      setShowPartnerSearch(true);
                      if (partnerOptions.length === 0) {
                        setPartnerLoading(true);
                        apiClient.httpInstance.get('/agencies_sellers')
                          .then(r => {
                            const opts = (r.data || []).filter(o => o.email).map(o => ({ ...o, nameSelect: o.name || o.email }));
                            setPartnerOptions(opts);
                          })
                          .finally(() => setPartnerLoading(false));
                      }
                    }}
                    style={{ background: 'transparent', border: `1px solid ${C.accent}`, borderRadius: 8, color: C.accent, padding: '8px 16px', fontSize: 13, cursor: 'pointer' }}
                  >
                    + Adicionar vendedor parceiro
                  </button>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <input
                      placeholder="Buscar por nome ou e-mail..."
                      value={partnerSearch}
                      onChange={e => setPartnerSearch(e.target.value)}
                      style={{
                        background: C.inner, border: `1px solid ${C.border}`, borderRadius: 8,
                        padding: '8px 12px', color: C.textMain, fontSize: 13, outline: 'none',
                      }}
                    />
                    {partnerLoading && <span style={{ color: C.textSec, fontSize: 12 }}>Carregando...</span>}
                    <div style={{ maxHeight: 180, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {partnerOptions
                        .filter(o => !partnerSearch || (o.nameSelect || '').toLowerCase().includes(partnerSearch.toLowerCase()) || (o.email || '').toLowerCase().includes(partnerSearch.toLowerCase()))
                        .slice(0, 30)
                        .map(o => (
                          <button
                            key={o.id + o.type}
                            onClick={() => {
                              const url = o.type === 'agency' ? `/agencies/${o.id}` : `/partner_sellers/${o.id}`;
                              apiClient.httpInstance.get(url).then(r => {
                                setPartnerSeller({ id: r.data.id, name: r.data.name || r.data.email, email: r.data.email, type: o.type });
                                setShowPartnerSearch(false);
                                setPartnerSearch('');
                              });
                            }}
                            style={{
                              background: C.inner, border: `1px solid ${C.border}`, borderRadius: 6,
                              padding: '8px 12px', color: C.textMain, fontSize: 13, cursor: 'pointer',
                              textAlign: 'left',
                            }}
                          >
                            {o.nameSelect} <span style={{ color: C.textSec, fontSize: 11 }}>({o.type})</span>
                          </button>
                        ))}
                    </div>
                    <button
                      onClick={() => { setShowPartnerSearch(false); setPartnerSearch(''); }}
                      style={{ alignSelf: 'flex-start', background: 'transparent', border: 'none', color: C.textSec, cursor: 'pointer', fontSize: 12 }}
                    >
                      Cancelar
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ── Botões ──────────────────────────────── */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <Button
            textcolor={C.textSec}
            padding="8px 20px"
            variant="outlined"
            bgColor="transparent"
            onClick={() => history.push(`/admin/orcamentos/detalhe/${presales_id}`)}
          >
            <Icon style={{ fontSize: 16, marginRight: 6 }}>arrow_back</Icon>
            Cancelar
          </Button>
          {!locked && (
            <Button
              textcolor="#0f1e35"
              padding="8px 24px"
              customcolor={C.accent}
              onClick={save}
              disabled={saving}
            >
              {saving ? 'Salvando...' : 'Salvar alterações'}
            </Button>
          )}
        </div>
      </div>

      {/* ── Modal: adicionar serviço ─────────────── */}
      {addModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 9999,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: C.card, borderRadius: 16, padding: '28px 24px',
            width: 480, maxWidth: '92vw', maxHeight: '85vh',
            display: 'flex', flexDirection: 'column', gap: 16,
            boxShadow: '0 8px 40px rgba(0,0,0,0.6)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: C.textMain, fontWeight: 700, fontSize: 16 }}>
                Adicionar serviço
              </span>
              <button onClick={() => setAddModal(false)} style={{ background: 'none', border: 'none', color: C.textSec, cursor: 'pointer' }}>
                <Icon>close</Icon>
              </button>
            </div>

            {/* Tipo */}
            <div style={{ display: 'flex', gap: 8 }}>
              {['tour', 'transfer'].map(t => (
                <button
                  key={t}
                  onClick={() => { setAddType(t); setAddSearch(''); setAddSelected(null); setAddDate(null); setAddCodFly(''); setAddTimeFly(null); }}
                  style={{
                    flex: 1, padding: '8px', borderRadius: 8, cursor: 'pointer',
                    border: `1px solid ${addType === t ? C.accent : C.border}`,
                    background: addType === t ? `${C.accent}22` : C.inner,
                    color: addType === t ? C.accent : C.textSec,
                    fontWeight: 600, fontSize: 13, textTransform: 'capitalize',
                  }}
                >
                  {t === 'tour' ? 'Passeio' : 'Transfer'}
                </button>
              ))}
            </div>

            {/* Busca */}
            <div>
              <label style={{ color: C.textSec, fontSize: 12, display: 'block', marginBottom: 6 }}>
                Buscar {addType === 'tour' ? 'passeio' : 'transfer'}
              </label>
              <input
                value={addSearch}
                onChange={e => { setAddSearch(e.target.value); setAddSelected(null); }}
                placeholder={`Nome do ${addType === 'tour' ? 'passeio' : 'transfer'}...`}
                style={inputStyle}
              />
            </div>

            {/* Resultados */}
            {addLoading && (
              <p style={{ color: C.textSec, fontSize: 12, textAlign: 'center', margin: 0 }}>
                Carregando serviços...
              </p>
            )}
            {!addLoading && !addSelected && (
              <div style={{
                background: C.inner, borderRadius: 8, border: `1px solid ${C.border}`,
                maxHeight: 200, overflowY: 'auto',
              }}>
                {addResults.length === 0 ? (
                  <p style={{ color: C.textSec, fontSize: 12, padding: '10px 14px', margin: 0 }}>
                    {addSearch.length >= 2 ? 'Nenhum resultado encontrado.' : 'Digite para filtrar...'}
                  </p>
                ) : addResults.map(r => (
                  <div
                    key={r.id}
                    onClick={() => { setAddSelected(r); setAddSearch(r.name ?? r.region_name ?? ''); }}
                    style={{
                      padding: '10px 14px', cursor: 'pointer', color: C.textMain, fontSize: 13,
                      borderBottom: `1px solid ${C.border}`,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#1e3a5f'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <span style={{ color: C.accent, fontSize: 10, marginRight: 6, textTransform: 'uppercase' }}>
                      {r.region_name ?? ''}
                    </span>
                    {r.name ?? r.region_name ?? `#${r.id}`}
                  </div>
                ))}
              </div>
            )}

            {addSelected && (
              <>
                {/* ── PASSEIO: data ── */}
                {addType === 'tour' && (
                  <DatePicker
                    label="Data do passeio"
                    value={addDate}
                    onChange={date => setAddDate(date)}
                    shouldDisableDate={date => !disableDates(date)}
                    disablePast
                  />
                )}

                {/* ── PASSEIO: modalidade (só se o tour tem tabela de privado) ── */}
                {addType === 'tour' && addPrivateValues.length > 0 && (
                  <div>
                    <label style={{ color: C.textSec, fontSize: 12, display: 'block', marginBottom: 4 }}>Modalidade</label>
                    <RadioGroup
                      row
                      value={addModality}
                      onChange={e => setAddModality(e.target.value)}
                    >
                      <FormControlLabel value="group"  control={<Radio style={{ color: C.accent }} />} label={<span style={{ color: C.textMain, fontSize: 13 }}>Em grupo</span>} />
                      <FormControlLabel value="single" control={<Radio style={{ color: C.accent }} />} label={<span style={{ color: C.textMain, fontSize: 13 }}>Passeio privado</span>} />
                    </RadioGroup>
                  </div>
                )}

                {/* ── TRANSFER: tipo In/Out + data + voo ── */}
                {addType === 'transfer' && (
                  <>
                    <div>
                      <label style={{ color: C.textSec, fontSize: 12, display: 'block', marginBottom: 4 }}>Tipo</label>
                      <RadioGroup
                        row
                        value={addTransferType}
                        onChange={e => setAddTransferType(e.target.value)}
                      >
                        <FormControlLabel value="Transfer In"  control={<Radio style={{ color: C.accent }} />} label={<span style={{ color: C.textMain, fontSize: 13 }}>Transfer In</span>} />
                        <FormControlLabel value="Transfer Out" control={<Radio style={{ color: C.accent }} />} label={<span style={{ color: C.textMain, fontSize: 13 }}>Transfer Out</span>} />
                      </RadioGroup>
                    </div>
                    <DatePicker
                      label="Data do transfer"
                      value={addDate}
                      onChange={date => setAddDate(date)}
                    />
                    <div style={{ display: 'flex', gap: 12 }}>
                      <div style={{ flex: 1 }}>
                        <TextField
                          className="inputRounded"
                          label="Código do voo"
                          variant="outlined"
                          type="text"
                          value={addCodFly}
                          onChange={e => setAddCodFly(e.target.value)}
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <TimePicker
                          label="Horário do voo"
                          value={addTimeFly}
                          onChange={time => setAddTimeFly(time)}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Passageiros */}
                <div style={{ display: 'flex', gap: 24 }}>
                  <div>
                    <label style={{ color: C.textSec, fontSize: 12, display: 'block', marginBottom: 8 }}>Adultos</label>
                    <Counter value={addAdults} min={1} onChange={setAddAdults} />
                    {addModality !== 'single' && addSelected?.price > 0 && (
                      <span style={{ color: C.textSec, fontSize: 11, marginTop: 4, display: 'block' }}>
                        {process.env.REACT_APP_CURRENCY} {Number(addSelected.price).toFixed(2).replace('.', ',')} /adulto
                      </span>
                    )}
                  </div>
                  {addType === 'tour' && (
                    <div>
                      <label style={{ color: C.textSec, fontSize: 12, display: 'block', marginBottom: 8 }}>Crianças</label>
                      <Counter value={addKids} onChange={setAddKids} />
                      {addModality !== 'single' && addSelected?.kids_price > 0 && (
                        <span style={{ color: C.textSec, fontSize: 11, marginTop: 4, display: 'block' }}>
                          {process.env.REACT_APP_CURRENCY} {Number(addSelected.kids_price).toFixed(2).replace('.', ',')} /criança
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Total preview + info desconto automático */}
                {(() => {
                  let base;
                  if (addType === 'tour' && addModality === 'single') {
                    // Privado: valor TOTAL da faixa (busca por adultos, mesma convenção da criação)
                    const band = addPrivateValues.find(v => Number(v.min_people) <= addAdults && addAdults <= Number(v.max_people))
                    if (!band) {
                      return (
                        <span style={{ color: '#f87171', fontSize: 12 }}>
                          Não existe intervalo de privado definido para {addAdults} {addAdults === 1 ? 'pessoa' : 'pessoas'}.
                        </span>
                      )
                    }
                    base = Number(band.value)
                  } else {
                    const baseAdults = addAdults * (addSelected?.price ?? 0)
                    const baseKids   = addKids   * (addSelected?.kids_price ?? 0)
                    base = baseAdults + baseKids
                  }
                  return base > 0 ? (
                    <div style={{ display: 'flex', gap: 24, alignItems: 'flex-end' }}>
                      <div>
                        <label style={{ color: C.textSec, fontSize: 12, display: 'block', marginBottom: 6 }}>Total</label>
                        <span style={{ color: C.accent, fontWeight: 700, fontSize: 16 }}>
                          {process.env.REACT_APP_CURRENCY} {base.toFixed(2).replace('.', ',')}
                        </span>
                      </div>
                      {addType === 'tour' && (
                        <div>
                          <label style={{ color: C.textSec, fontSize: 12, display: 'block', marginBottom: 6 }}>Desc. automático</label>
                          <span style={{ fontSize: 11, color: '#6e8eb0' }}>
                            {autoDiscountLabel(addSelected?.region_name, presale?.created_at)}
                          </span>
                        </div>
                      )}
                    </div>
                  ) : null
                })()}

                <button
                  onClick={submitAddBudget}
                  disabled={addSaving}
                  style={{
                    background: C.accent, border: 'none', borderRadius: 10,
                    padding: '12px', color: '#0f1e35', fontWeight: 700,
                    fontSize: 14, cursor: addSaving ? 'not-allowed' : 'pointer',
                    opacity: addSaving ? 0.7 : 1,
                  }}
                >
                  {addSaving ? 'Adicionando...' : 'Confirmar e adicionar'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default Update;
